import { useQueryParams } from '@app/hooks/useQuestionaireRoute';
import OutputView, { NodeReplace } from '@components/block/OutputView/OutputView';
import { Stack, Switch, Typography } from '@mui/material';

import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { findParentNodes, getNodeText } from '@app/utils/getNodeText';
import { DocumentShareToken, parseJwt } from '@app/utils/parseJwt';

import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { FileView } from '@components/block/FileView/FileView';
import LangSelect from '@components/block/Header/LangSelect';
import { Logo } from '@components/block/Header/Logo';
import { AlertDialog, Button, Field, Input } from '@components/ui';
import {
  useConfirmCustomTrainingMutation,
  useCustomTrainingByTokenQuery,
} from '@dieterApi/customTraining/useCustomTrainings';
import { StakeholderType, useAddStakeholderMutation } from '@dieterApi/employee/useStakeholderAddMutation';
import { useFileDownloadQuery } from '@dieterApi/file/useFileDownloadQuery';
import { useAddProcessingConsentMutation } from '@dieterApi/questionnaire/useAddProcessingConsentMutation';
import { useConfirmProcessingConsentMutation } from '@dieterApi/questionnaire/useConfirmProcessingConsentMutation';
import { useQuestionnaireDocumentConfirmMutation } from '@dieterApi/questionnaire/useQuestionnaireDocumentConfirmMutation';
import { ConsentFrom } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { useGetShareTokenJWTQuery } from '@dieterApi/sharetoken/useGetShareTokenJWTQuery';
import { Modal, Paper } from '@mui/material';
import { Download } from 'lucide-react';
import debounce from 'lodash/debounce';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDashboard } from '../Dashboard/useDashboard';
import './documentshare.sass';

export interface RouteParams {
  localQuestionnaireId?: string;
}

export interface RouteParamsWithQuery extends RouteParams {
  documentNumber?: number;
}

interface ConsentDetails {
  purpose: string;
  data: string[];
  consent: boolean;
}

const ConsenFromMapping: Record<ConsentFrom, StakeholderType> = {
  Bewerber: StakeholderType.Applicant,
  Mitarbeiter: StakeholderType.Employee,
  Vertragspartner: StakeholderType.ContractPartner,
};

export default function DocumentShare() {
  const { whitelabelConfig } = useUserWithCompany();
  const { t } = useTranslation();
  const query = useQueryParams();
  const { create: createApplication, open: openQuestionnaire } = useDashboard();
  const { navigation, setNavigation } = useNavigation();

  const token = query.get('token') || '';
  const { data: shareTokenJWT } = useGetShareTokenJWTQuery({
    variables: { id: token },
  });

  const jwt = shareTokenJWT?.getShareTokenJWT?.jwt;
  const tokenInfo = jwt ? parseJwt<DocumentShareToken>(jwt)! : undefined;

  const isQuestionnaireType = tokenInfo?.type === 'Questionnaire';
  const isListQuestionnaireType = tokenInfo?.type === 'ListQuestionnaire';
  const isUniqueQuestionnaireType = tokenInfo?.type === 'UniqueQuestionnaire';
  const isConsentType = tokenInfo?.type === 'ConsentForm';
  const isCustomTrainingType = tokenInfo?.type === 'CustomTraining';
  const { data: customTrainingData, error: customTrainingError } = useCustomTrainingByTokenQuery(
    jwt,
    !isCustomTrainingType
  );
  const customTraining = customTrainingData?.getCustomTrainingByToken;
  const [confirmCustomTraining] = useConfirmCustomTrainingMutation();
  const [downloadFile] = useFileDownloadQuery();

  // Decode pre-filled name from URL param (base64-encoded, added by personalized link generator)
  const nameParam = query.get('name') || '';
  const prefilledName = nameParam
    ? (() => {
        try {
          return decodeURIComponent(escape(atob(nameParam)));
        } catch {
          return '';
        }
      })()
    : '';

  const [value, setValue] = useState(prefilledName);
  const [hasRead, setHasRead] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [consentAlertOpen, setConsentAlertOpen] = useState(false);
  const [atLeastOneConsent, setAtLeastOneConsent] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDocument] = useQuestionnaireDocumentConfirmMutation();
  const [confirmProcessingConsent] = useConfirmProcessingConsentMutation();
  const [addStakeholder] = useAddStakeholderMutation();
  const [addProcessingConsent] = useAddProcessingConsentMutation();
  // There is a timing problem in setting this to true
  // apparently the navigation context is not yet set
  useEffect(() => {
    if (navigation.isPrintView) return;
    setNavigation((nav) => void (nav.isPrintView = true));
  }, [navigation]);

  useEffect(() => {
    return () => {
      setNavigation((nav) => void (nav.isPrintView = false));
    };
  }, []);
  // const thisDoc = user?.documents.find((doc) => doc.id === currentParams.documentId);
  const handleClick = () => {
    if (!tokenInfo) return;

    // If confirmation is required, validate name
    if (tokenInfo.withConfirmation) {
      if (!value) {
        enqueueSnackbar(
          t('route.document_share.route.document_share.snackbar.missing_name', 'Bitte geben Sie einen Namen ein.'),
          { variant: 'error' }
        );
        return;
      }
      if (!token) {
        enqueueSnackbar(t('common.error.invalid_auth_token', 'Ungültiger Zugriffstoken.'), { variant: 'error' });
        return;
      }
    } else {
      setAccepted(true);
    }

    // Handle different token types
    switch (tokenInfo.type) {
      case 'CustomTraining':
        jwt && setAccepted(true);
        break;
      case 'Document':
      case 'ConsentForm':
        jwt && setAccepted(true);
        if (isConsentType && tokenInfo.consentFrom) {
          addStakeholder({
            variables: {
              token: jwt || '',
              name: value,
              type: ConsenFromMapping[tokenInfo.consentFrom],
            },
          });
        }

        break;
      case 'Questionnaire':
      case 'UniqueQuestionnaire':
        if (tokenInfo.applicationId) {
          createApplication(
            tokenInfo.applicationId,
            tokenInfo.documentNumber,
            true,
            undefined, // userValueIndex
            jwt,
            token, // urlToken
            value, // name
            tokenInfo.type === 'UniqueQuestionnaire' ? true : false // onlyIfNotExists
          );
        }
        break;
      case 'ListQuestionnaire':
        if (tokenInfo.localQuestionnaireId) {
          openQuestionnaire(
            tokenInfo.localQuestionnaireId,
            undefined, //section
            false, //revisit
            jwt,
            token // urlToken
          );
        }
        break;
      default:
        const _exhaustiveCheck: never = tokenInfo.type;
        break;
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // determine if user has scrolled to bottom
    const element = e.target as HTMLDivElement;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      setHasRead(true);
    }
  };

  const handleHasReadClick = () => {
    if (isConsentType) {
      if (!atLeastOneConsent) {
        setConsentAlertOpen(true);
      } else {
        jwt &&
          confirmProcessingConsent({ variables: { token: jwt, name: value } })
            .then(() => {
              enqueueSnackbar(t('route.document_share.snackbar.has_read.success'), {
                variant: 'success',
              });
              setHasConfirmed(true);
            })
            .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
      }
    } else if (isCustomTrainingType) {
      jwt &&
        confirmCustomTraining({ variables: { token: jwt, name: value } })
          .then(() => {
            enqueueSnackbar(t('route.document_share.snackbar.has_read.success'), {
              variant: 'success',
            });
            setHasConfirmed(true);
          })
          .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
    } else {
      jwt &&
        confirmDocument({ variables: { token: jwt, name: value } })
          .then(() => {
            enqueueSnackbar(t('route.document_share.snackbar.has_read.success'), {
              variant: 'success',
            });
            setHasConfirmed(true);
          })
          .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
    }
  };

  const handleScrollDebounced = debounce(handleScroll, 100, { leading: true, trailing: true });

  const document_with_confirmation = tokenInfo?.withConfirmation
    ? t('route.document_share.conditionals.document_with_confirmation', 'zur Kenntnisnahme ')
    : '';

  const handleTableCheckbox = (consent: ConsentDetails) => {
    if (!tokenInfo?.consentFrom) return;
    if (consent.consent) setAtLeastOneConsent(true);
    if (jwt) {
      addProcessingConsent({
        variables: {
          name: value,
          consentFrom: tokenInfo?.consentFrom,
          token: jwt,
          purpose: consent.purpose,
          dataCategories: consent.data,
          consent: consent.consent,
        },
      }).catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
    }
  };

  const useRenderDieterTableCheckboxNode: NodeReplace['renderNode'] = (node, root) => {
    const [status, setStatus] = useState<ConsentDetails>({ consent: false, purpose: '', data: [] });
    const userInteracted = useRef(false);

    useEffect(() => {
      if (!status.purpose) return;
      if (!userInteracted.current) return;
      handleTableCheckbox(status);
    }, [status]);

    useEffect(() => {
      const nodes = findParentNodes(node.key, root);
      if (!nodes) return;
      const trow = nodes.reverse().find((n) => n.type === 'tr');
      if (trow?.type === 'text' || !trow?.children) return;
      const purpose = getNodeText(trow.children[0]) || '';
      // @ts-expect-error
      const data = trow?.children?.[1].children.map((n) => getNodeText(n)).filter(Boolean);
      setStatus({ ...status, purpose, data });
    }, []);

    return (
      <div className="w-full h-full grid place-items-center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>{t('common.no', 'no')}</Typography>
          <Switch
            checked={status.consent}
            disabled={hasConfirmed}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              userInteracted.current = true;
              setStatus({ ...status, consent: e.target.checked });
            }}
          />
          <Typography>{t('common.yes', 'yes')}</Typography>
        </Stack>
      </div>
    );
  };

  return !accepted ? (
    <div className="dtDocumentShare flex flex-col gap-8">
      <div className="fixed top-6 right-6">
        <LangSelect inHeader={false} />
      </div>
      <div className="header flex gap-[10px]">
        <h1 className="text-3xl font-medium">
          {isUniqueQuestionnaireType || isListQuestionnaireType
            ? t('route.document_share.accept_form.title.unique_questionnaire', 'Einmalige Schulung')
            : isQuestionnaireType
              ? t('route.document_share.accept_form.title.questionnaire', 'Schulungsanforderung')
              : isConsentType
                ? t('route.document_share.accept_form.title.consent_form')
                : isCustomTrainingType
                  ? t('route.document_share.accept_form.title.custom_training', 'Schulung')
                  : t('route.document_share.accept_form.title.document', 'Dokumentenfreigabe')}
        </h1>
        <Logo clickable={false} maxWidth="max-w-64" variant="light" />
      </div>
      {tokenInfo ? (
        <>
          {isUniqueQuestionnaireType || isListQuestionnaireType ? (
            <Trans
              t={t}
              i18nKey="route.document_share.accept_form.description.unique_questionnaire"
              shouldUnescape
              values={tokenInfo}
            >
              <p>
                {/* @ts-expect-error */}
                {{ companyName: tokenInfo.companyName }} lädt dich ein die {/* @ts-expect-error */}
                <strong>{{ documentTitle: tokenInfo.documentTitle }}</strong> durchzuarbeiten.
              </p>
            </Trans>
          ) : isQuestionnaireType ? (
            <Trans
              t={t}
              i18nKey="route.document_share.accept_form.description.questionnaire"
              shouldUnescape
              values={tokenInfo}
            >
              <p>
                {/* @ts-expect-error */}
                {{ companyName: tokenInfo.companyName }} lädt dich ein die {/* @ts-expect-error */}
                <strong>{{ documentTitle: tokenInfo.documentTitle }}</strong> durchzuarbeiten.
              </p>
            </Trans>
          ) : isCustomTrainingType ? (
            customTrainingError ? (
              <p>{customTrainingError.message}</p>
            ) : (
              <>
                <Trans
                  t={t}
                  i18nKey="route.document_share.accept_form.description.custom_training"
                  shouldUnescape
                  values={tokenInfo}
                />
                {customTraining?.description && (
                  <p className="whitespace-pre-line border-l-4 border-gray-200 pl-3 text-gray-700">
                    {customTraining.description}
                  </p>
                )}
              </>
            )
          ) : (
            <Trans
              t={t}
              i18nKey={
                isConsentType
                  ? 'route.document_share.accept_form.description.consent_form'
                  : 'route.document_share.accept_form.description.document'
              }
              shouldUnescape
              values={{
                ...tokenInfo,
                withConfirmation: document_with_confirmation,
              }}
            />
          )}
          {tokenInfo.withConfirmation && (
            <>
              {!prefilledName && (
                <Trans
                  t={t}
                  i18nKey="route.document_share.accept_form.description.confirmation_request"
                  shouldUnescape
                  values={{
                    action: t(
                      `route.document_share.conditionals.confirmation_request_action.${tokenInfo?.type || 'Document'}`
                    ),
                  }}
                />
              )}
              <Field className="mb-5" htmlFor="fullname">
                <Input
                  required
                  onChange={(e) => {
                    if (!prefilledName) setValue(e.target.value);
                  }}
                  onKeyPress={(e) => (e.key === 'Enter' ? handleClick() : null)}
                  type="text"
                  placeholder={t('route.document_share.accept_form.fields.fullname.placeholder')}
                  value={value}
                  id="fullname"
                  data-testid="fullname-field"
                  readOnly={!!prefilledName}
                  style={
                    prefilledName ? { backgroundColor: 'transparent', cursor: 'default', opacity: 0.8 } : undefined
                  }
                />
              </Field>
              <div className="privacy-note text-sm">
                <Trans
                  t={t}
                  i18nKey="route.document_share.accept_form.privacy_link"
                  components={{
                    'router-link': (
                      <a
                        href={whitelabelConfig?.privacyUrl || '/privacy'}
                        target="_blank"
                        className="underline"
                        data-testid="privacy-link"
                      />
                    ),
                  }}
                />
              </div>
            </>
          )}
          <Button
            onClick={handleClick}
            disabled={(tokenInfo.withConfirmation && !value) || (isCustomTrainingType && !customTraining)}
            data-testid={'button-submit'}
          >
            {t(`route.document_share.accept_form.submit.${tokenInfo?.type || 'Document'}`)}
          </Button>
        </>
      ) : (
        <p>{t('route.document_share.invalid_url')}</p>
      )}
    </div>
  ) : (
    <>
      <Modal
        open={true}
        hideBackdrop={true}
        disableEnforceFocus={true}
        data-testid="doc-modal"
        disablePortal
        sx={{ zIndex: '40' }}
      >
        <div className="h-screen w-screen flex flex-col items-center py-4 gap-4">
          {tokenInfo?.withConfirmation && (
            <div className="w-[95vw] md:w-[80vw] max-w-[800px] shrink-0">
              <Button onClick={handleHasReadClick} disabled={hasConfirmed} data-testid={'button-hasread'}>
                {hasConfirmed
                  ? t('route.document_share.tooltip.button_label_confirmed')
                  : isConsentType
                    ? t('route.document_share.tooltip.button_label_consent')
                    : t('route.document_share.tooltip.button_label')}
              </Button>
            </div>
          )}
          {isCustomTrainingType && customTraining ? (
            <Paper
              className="overflow-y-auto overflow-x-hidden w-[95vw] md:w-[80vw] max-w-[800px] flex-1 min-h-0 pointer-events-auto flex flex-col"
              elevation={10}
              data-testid="custom-training-document"
            >
              <div className="flex-1">
                <FileView fileId={customTraining.fileId} token={jwt} />
              </div>
              <div className="sticky bottom-0 flex justify-center py-2 bg-white/90">
                <button
                  className="underline text-primary-root text-sm flex items-center gap-2"
                  onClick={() => jwt && downloadFile({ variables: { id: customTraining.fileId, token: jwt } })}
                >
                  <Download size={14} />
                  {t('route.document_share.download_document', 'Dokument herunterladen')}
                </button>
              </div>
            </Paper>
          ) : tokenInfo?.localQuestionnaireId && !isListQuestionnaireType ? (
            <Paper
              className="overflow-y-auto overflow-x-hidden w-[95vw] md:w-[80vw] max-w-[800px] flex-1 min-h-0 pointer-events-auto"
              elevation={10}
              onScroll={handleScrollDebounced}
            >
              <OutputView
                localQuestionnaireId={tokenInfo.localQuestionnaireId}
                documentNumber={tokenInfo?.documentNumber}
                token={jwt}
                consumption={false}
                replace={[{ classNameMatch: 'DieterTableCheckbox', renderNode: useRenderDieterTableCheckboxNode }]}
              />
            </Paper>
          ) : (
            <div
              className="flex items-center justify-center w-[95vw] md:w-[80vw] max-w-[800px] flex-1"
              style={{ minHeight: '200px' }}
            >
              Loading...
            </div>
          )}
        </div>
      </Modal>
      <AlertDialog
        acceptText={t('route.document_share.consent_alert_button')}
        open={consentAlertOpen}
        setOpen={setConsentAlertOpen}
        message={t('route.document_share.consent_alert_message')}
        onCancel={() => setConsentAlertOpen(false)}
        onAccept={() => {
          setConsentAlertOpen(false);
          jwt &&
            confirmProcessingConsent({ variables: { token: jwt, name: value } })
              .then(() => {
                enqueueSnackbar(t('route.document_share.snackbar.has_read.success'), {
                  variant: 'success',
                });
                setHasConfirmed(true);
              })
              .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
        }}
      />
    </>
  );
}
